import AbstractFetch from '~/repositories/AbstractFetch';
import type { IFetchOptions, IResponse } from '~/types/repositories';
import type { IFavoritesResponse, IFavoritesCountResponse, IGetFavoritesQuery } from '~/modules/favorites/types/favorites';

export default class Favorites extends AbstractFetch {
  private static readonly FAVORITES = 'v1/favorites/';

  constructor(fetchOptions: IFetchOptions) {
    super(fetchOptions.baseURL, fetchOptions.fetch);
  }

  /**
   * Создает строку запроса для получения избранных элементов на основе заданных параметров.
   *
   * @param {IGetFavoritesQuery} query - Объект, содержащий параметры запроса для формирования строки запроса.
   * @param {number} [query.page] - Номер страницы результатов для получения.
   * @param {number} [query.count] - Количество элементов на страницу для получения.
   * @returns {string} URL-кодированная строка запроса, представляющая заданные параметры.
   */
  buildFavoritesQueryString = (query: IGetFavoritesQuery): string => {
    const params = new URLSearchParams();

    if (query.page !== undefined) {
      params.append('page', query.page.toString());
    }

    if (query.count !== undefined) {
      params.append('count', query.count.toString());
    }

    return params.toString();
  };

  /**
   * Получает список избранных товаров
   * @param id - ID пользователя
   * @param query - передается { page: number, count: number }
   */
  async getFavorites<T>(id: number, query?: IGetFavoritesQuery): Promise<IResponse<IFavoritesResponse<T>>> {
    if (query) {
      const queryString = this.buildFavoritesQueryString(query);
      return this.fetch(`${Favorites.FAVORITES}${id}/products?${queryString}`);

    } else {
      return this.fetch(`${Favorites.FAVORITES}${id}/products`);
    }
  }

  /**
   * Добавляет товар в избранное
   * @param id - ID пользователя
   * @param productId - ID товара
   */
  async addFavorite(id: number, productId: number): Promise<IResponse<void>> {
    return this.fetch(`${Favorites.FAVORITES}${id}/products`, {
      method: 'POST',
      body: { id: productId },
    });
  }

  /**
   * Удаляет товар из избранного
   * @param id - ID пользователя
   * @param productId - ID товара
   */
  async removeFavorite(id: number, productId: number): Promise<IResponse<void>> {
    return this.fetch(`${Favorites.FAVORITES}${id}/products/${productId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Получает количество избранных товаров
   * @param id - ID пользователя
   */
  async getFavoritesCount(id: number): Promise<IResponse<IFavoritesCountResponse>> {
    return this.fetch(`${Favorites.FAVORITES}${id}/count`);
  }
}
